import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../core/auth/auth';
import { passwordValidators } from '../../core/auth/password-validators';
import { PasswordField, PasswordInput } from '../../shared/password-field/password-field';

@Component({
  selector: 'app-register',
  imports: [ReactiveFormsModule, RouterLink, PasswordField, PasswordInput],
  templateUrl: './register.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './register.css',
})
export class Register {
  private readonly auth = inject(AuthService);

  protected readonly submitting = signal(false);
  protected readonly submitted = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  readonly form = new FormGroup({
    displayName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: passwordValidators,
    }),
    // F-034 — consent + 18+ attestation. requiredTrue means the existing
    // `form.invalid` guard in submit() already blocks an unchecked box.
    acceptTerms: new FormControl(false, {
      nonNullable: true,
      validators: [Validators.requiredTrue],
    }),
  });

  async submit(): Promise<void> {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);
    try {
      await this.auth.register(this.form.getRawValue());
      this.submitted.set(true);
    } catch (err) {
      this.errorMessage.set(
        err instanceof HttpErrorResponse && err.status === 429
          ? 'Too many attempts — please wait a minute and try again.'
          : "Something went wrong on our end — give it another try in a moment.",
      );
    } finally {
      this.submitting.set(false);
    }
  }
}
